import { NDT_IMZA_EN_AZ_SEVIYE, NDT_SEVIYELERI, NDT_YONTEMLERI } from "./kutuk-belge.js";
import { yetkiliMi } from "./yetki.js";
/** Adım adındaki yöntem kısaltmaları: büyük harf DUYARLI ve sözcük sınırlı ("NDT", "post-test", "Cutting" içindeki harfler eşleşmez). */
const YONTEM_KALIBI = /\b(MT|MPI|PT|DPI|UT|RT|VT)\b/g;
/** Kısaltma → sertifika yöntemi: MPI MT sayılır, DPI PT sayılır. */
const KISALTMA_YONTEMI = { MT: "MT", MPI: "MT", PT: "PT", DPI: "PT", UT: "UT", RT: "RT", VT: "VT" };
/**
 * Raporun yöntemleri: veri olarak tutulmaz, Component adım raporunda adım ADINDAN okunur (S3: adlar değişmez). Sonuç tekildir ve addaki
 * İLK GÖRÜLME sırasındadır: "NDT + Hardness (UT+MPI)" → [UT, MT], "NDT: RT + DPI" → [RT, PT]. Kısaltma taşımayan adda ("Re-NDT",
 * "NDT + Hardness (post-PWHT)") ve adım olmayan raporda (Finishing hat NDT raporu) boş döner: boşken yöntem aranmaz. "Hardness" yöntem
 * vermez (sertlik ölçümü bu sertifikaların kapsamında değil). Bilinen sınır: "MT" bu depoda Main Tube için de kullanılır; adında "MT"
 * geçen her adım MT okunur.
 */
export function adimYontemleri(adimAd) {
    const sonuc = [];
    for (const m of (adimAd ?? "").matchAll(YONTEM_KALIBI)) {
        const yontem = KISALTMA_YONTEMI[m[1] ?? ""];
        if (yontem && !sonuc.includes(yontem))
            sonuc.push(yontem);
    }
    return sonuc;
}
/**
 * Ölçüm günü: verilen anın Europe/Istanbul takvim günü ("YYYY-MM-DD"). Yükleme pencereleri `istanbulGunu()` ile bugünü alır (kullanıcının
 * cihaz saatinden). Sertifikanın bitiş günü bu fonksiyondan GEÇİRİLMEZ: o, kayıtlı ISO metnin ilk 10 karakteridir (kolon gün başı UTC tutar).
 */
export function istanbulGunu(an = new Date()) {
    const parcalar = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(an);
    const al = (tip) => parcalar.find((p) => p.type === tip)?.value ?? "";
    return `${al("year")}-${al("month")}-${al("day")}`;
}
const yontemMi = (v) => NDT_YONTEMLERI.includes(v ?? "");
const seviyeMi = (v) => NDT_SEVIYELERI.includes(v ?? "");
/**
 * Bir sertifikanın ölçüm günündeki (`gun`: "YYYY-MM-DD") durumu; sırayla ilk uyan:
 * - kayit_eksik: yöntem ya da seviye listede değil (boş, D-062 döneminin satırı, ham içe aktarımın liste dışı değeri).
 * - seviye_dusuk: Seviye 1 (bitişi geçmiş olsa da).
 * - suresi_gecmis: geçerlilik sonu dolu ve ilk 10 karakteri günden KÜÇÜK (metin karşılaştırması; bitiş günü dahildir).
 * - gecerli: ötekiler. Geçerlilik sonu BOŞ sertifikanın süresi geçmemiş sayılır. Başlangıç tarihi ölçülmez.
 */
export function sertifikaDurumu(s, gun) {
    if (!yontemMi(s.yontem) || !seviyeMi(s.seviye))
        return "kayit_eksik";
    if (Number(s.seviye) < NDT_IMZA_EN_AZ_SEVIYE)
        return "seviye_dusuk";
    if (s.gecerlilikSonu && s.gecerlilikSonu.slice(0, 10) < gun)
        return "suresi_gecmis";
    return "gecerli";
}
/**
 * Seçilen imzalayanların yeterliliği. Boş liste = yeterli; en çok imzalayan başına BİR bulgu, imzalayan sırasıyla.
 *
 * Yeterli: HER imzalayanın en az bir ilgili ve geçerli sertifikası vardır. İlgili sertifika: kaydı eksik olmayan ve (`yontemler` boşsa
 * her yöntem; doluysa yöntemi `yontemler`de olan). Birleşik adımda (UT+MT, RT+DPI) adımdaki yöntemlerden BİRİ yeter; her yöntemin
 * karşılanması aranmaz (aynı adıma yöntem başına ayrı PDF yüklenebilir, imza PDF'e aittir). Aynı yöntemde birden çok sertifikadan biri
 * geçerliyse yeter.
 *
 * Yetersiz imzalayanın nedeni, sırayla ilk uyan:
 * 1. suresi_gecmis: ilgili sertifikalarından süresi geçmiş olan var → bitişi en geç olanın yöntemi ve günü (eşitlikte sırada önce gelen yöntem).
 * 2. seviye_dusuk: ilgili sertifikası var ve hepsi Seviye 1 → o sertifikaların tekil yöntemleri.
 * 3. kayit_eksik: ilgili sertifikası yok, yöntemi ya da seviyesi yazılmamış sertifikası var.
 * 4. yontem_yok (yalnız `yontemler` doluyken): ilgili ve kaydı eksik sertifikası yok, başka yöntemde sertifikası var → aranan yöntemler.
 * 5. sertifika_yok: hiç etkin sertifikası yok.
 * Yöntem sırası `yontemler`in sırasıdır (adım adındaki ilk görülme); `yontemler` boşsa NDT_YONTEMLERI sırası.
 *
 * `gun`: ölçüm günü, raporun yüklendiği gün ("YYYY-MM-DD"; `istanbulGunu()`). `sertifikalar`: inspektörün yalnız ETKİN NDT sertifikaları.
 */
export function imzalayanYeterliligi(g) {
    const yontemAranir = g.yontemler.length > 0;
    const sira = yontemAranir ? g.yontemler : NDT_YONTEMLERI;
    const bulgular = [];
    for (const kisi of g.imzalayanlar) {
        const ilgili = [];
        let kayitEksik = false;
        for (const s of kisi.sertifikalar) {
            const durum = sertifikaDurumu(s, g.gun);
            if (durum === "kayit_eksik" || !yontemMi(s.yontem))
                kayitEksik = true;
            else if (!yontemAranir || g.yontemler.includes(s.yontem))
                ilgili.push({ yontem: s.yontem, bitis: (s.gecerlilikSonu ?? "").slice(0, 10), durum });
        }
        if (ilgili.some((x) => x.durum === "gecerli"))
            continue;
        const bulgu = (neden, yontemler = [], bitis = null) => bulgular.push({ inspektorId: kisi.id, ad: kisi.ad, neden, yontemler, bitis });
        const gecmis = ilgili.filter((x) => x.durum === "suresi_gecmis");
        if (gecmis.length) {
            const son = gecmis.reduce((a, b) => (b.bitis > a.bitis || (b.bitis === a.bitis && sira.indexOf(b.yontem) < sira.indexOf(a.yontem)) ? b : a));
            bulgu("suresi_gecmis", [son.yontem], son.bitis);
        }
        else if (ilgili.length)
            bulgu("seviye_dusuk", sira.filter((y) => ilgili.some((x) => x.yontem === y)));
        else if (kayitEksik)
            bulgu("kayit_eksik");
        else if (kisi.sertifikalar.length)
            bulgu("yontem_yok", [...g.yontemler]);
        else
            bulgu("sertifika_yok");
    }
    return bulgular;
}
/**
 * Neden cümlelerinin İngilizce kalıpları (sözlük anahtarları). {0} = inspektörün adı; {1} = yöntem(ler), " / " ile birleşik ("MT",
 * "UT / MT"; çevrilmez); {2} = bitiş günü.
 */
export const YETERLILIK_KALIPLARI = {
    suresi_gecmis: "{0}: {1} certificate expired on {2}",
    seviye_dusuk: "{0}: {1} certificate is Level 1",
    kayit_eksik: "{0}: method or level is not recorded on the certificate",
    yontem_yok: "{0}: no {1} certificate",
    sertifika_yok: "{0}: no NDT certificate",
};
/** Bulgunun cümlesi: kalıp ve yer tutucu değerleri. Gün "YYYY-MM-DD" ham döner; ekran kendi tarih biçimiyle yazar (`degerler[2]`). */
export function yeterlilikCumlesi(b) {
    const kalip = YETERLILIK_KALIPLARI[b.neden];
    const yontemler = b.yontemler.join(" / ");
    switch (b.neden) {
        case "suresi_gecmis":
            return { kalip, degerler: [b.ad, yontemler, b.bitis ?? ""] };
        case "seviye_dusuk":
        case "yontem_yok":
            return { kalip, degerler: [b.ad, yontemler] };
        case "kayit_eksik":
        case "sertifika_yok":
            return { kalip, degerler: [b.ad] };
    }
}
/**
 * Sertifika özetini (yöntem, seviye, bitiş günü) gören roller: raporu yükleyebilenler (var olan "evrak" ya da "ndtYukle" yetkisi: saha
 * inspektörü; PM ve sistem yöneticisi her zaman). Öteki rollerde alan yanıtta yoktur. Yeni rol ya da yetki anahtarı değildir.
 */
export const sertifikaOzetiGorurMu = (rol) => yetkiliMi(rol, "evrak") || yetkiliMi(rol, "ndtYukle");
//# sourceMappingURL=ndt-yeterlilik.js.map